import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type Board } from '@air/api/types';
import { useAirModal } from '@air/provider-modal';
import { selectedItemsSelector } from '@air/redux-selected-items';

import { BatchExportImagesModal } from '~/components/ExportImagesModal/BatchExportImagesModal';
import { GalleryItemType } from '~/components/Gallery/types';
import { DownloadButton } from '~/components/v2/components/shared/selectionBar/DownloadButton';
import { ShareButton } from '~/components/v2/components/shared/selectionBar/ShareButton';
import { useGetPublicAssetMenuOptionsCallback } from '~/components/v2/hooks/useGetPublicAssetMenuOptionsCallback';
import { useGetPublicBoardMenuOptionsCallback } from '~/components/v2/hooks/useGetPublicBoardMenuOptionsCallback';
import { usePublicSelectionMenuOptions } from '~/components/v2/hooks/usePublicSelectionMenuOptions';
import {
  hasOnlyClipsSelectedSelector,
  selectedBoardsSelector,
  selectedFileClipsSelector,
  selectedMediaClipsSelector,
} from '~/store/selectedItems/selectors';
import { isClipExportable } from '~/utils/Imgix';
import { getDropdownMenuOption } from '~/utils/menuOptions/getDropdownMenuOption';

export type UsePublicSelectionBarMenuProps = {
  canDownload: boolean;
  canDownloadAsset: boolean;
  handleBulkDownload: () => void;
  handleBoardDownload: (boardId: string) => void;
  handleBoardShare: (board: Board) => void;
  handleAssetDownload: (id: string) => void;
  handleClipShare: (clipId: string) => void;
};

export const usePublicSelectionBarMenu = ({
  canDownload,
  canDownloadAsset,
  handleBulkDownload,
  handleBoardDownload,
  handleBoardShare,
  handleAssetDownload,
  handleClipShare,
}: UsePublicSelectionBarMenuProps) => {
  // Selectors
  const selectedItems = useSelector(selectedItemsSelector);
  const selectedBoardItems = useSelector(selectedBoardsSelector);
  const selectedMediaClipItems = useSelector(selectedMediaClipsSelector);
  const selectedFileItems = useSelector(selectedFileClipsSelector);
  const hasOnlyClipsSelected = useSelector(hasOnlyClipsSelectedSelector);

  const [showBatchExportImagesModal] = useAirModal(BatchExportImagesModal);

  // Menu options callbacks
  const { getBoardMenuOptionsCallback } = useGetPublicBoardMenuOptionsCallback();
  const { getAssetMenuOptionsCallback } = useGetPublicAssetMenuOptionsCallback();
  const { getSelectionMenuOptions } = usePublicSelectionMenuOptions();

  return useMemo(() => {
    let options = getSelectionMenuOptions('action-bar', true);
    let downloadButton = canDownload ? (
      <DownloadButton hasMultiple={selectedItems.length > 1} onClick={handleBulkDownload} />
    ) : null;
    let shareButton = null;

    // Handle single item selection
    if (selectedItems.length === 1) {
      const selectedItem = selectedItems[0];

      if (selectedItem.type === GalleryItemType.board) {
        const board = selectedBoardItems[0];
        options = getBoardMenuOptionsCallback({ item: board })({ trackLocation: 'action-bar' });
        downloadButton = canDownload ? (
          <DownloadButton hasMultiple={false} onClick={() => handleBoardDownload(board.id)} />
        ) : null;
        shareButton = <ShareButton onClick={() => handleBoardShare(board)} />;
      } else if (selectedItem.type === GalleryItemType.asset) {
        const clip = selectedMediaClipItems[0];
        options = getAssetMenuOptionsCallback({ item: clip })({ trackLocation: 'action-bar' });
        downloadButton = canDownloadAsset ? (
          <DownloadButton hasMultiple={false} onClick={() => handleAssetDownload(clip.id)} />
        ) : null;
        shareButton = <ShareButton onClick={() => handleClipShare(clip.id)} />;
      } else if (selectedItem.type === GalleryItemType.file) {
        const file = selectedFileItems[0];
        options = getAssetMenuOptionsCallback({ item: file })({ trackLocation: 'action-bar' });
        downloadButton = canDownloadAsset ? (
          <DownloadButton hasMultiple={false} onClick={() => handleAssetDownload(file.id)} />
        ) : null;
        shareButton = <ShareButton onClick={() => handleClipShare(file.id)} />;
      }
    }

    // Handle multiple clips selection
    else if (hasOnlyClipsSelected && canDownload) {
      const allSelectedClips = [...selectedMediaClipItems, ...selectedFileItems];
      const hasExportableClip = allSelectedClips.some((clip) => isClipExportable(clip));
      if (hasExportableClip) {
        options = [
          ...options,
          {
            ...getDropdownMenuOption('export-as'),
            label: 'Export selection as',
            onSelect: () => showBatchExportImagesModal({ clips: allSelectedClips }),
            type: 'item',
          },
        ];
      }
    }

    return {
      menuOptions: options,
      SuffixBarButtons: () => (
        <>
          {downloadButton}
          {shareButton}
        </>
      ),
    };
  }, [
    getSelectionMenuOptions,
    canDownload,
    selectedItems,
    handleBulkDownload,
    selectedBoardItems,
    getBoardMenuOptionsCallback,
    handleBoardDownload,
    handleBoardShare,
    selectedMediaClipItems,
    getAssetMenuOptionsCallback,
    canDownloadAsset,
    handleAssetDownload,
    handleClipShare,
    selectedFileItems,
    hasOnlyClipsSelected,
    showBatchExportImagesModal,
  ]);
};
